package com.rosario.juridico.ui.screens

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import androidx.navigation.compose.currentBackStackEntryAsState
import com.rosario.juridico.marketplace.model.FuerosJudiciales
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.platform.FirebaseProxy


// ── Paleta jurídica profesional ───────────────────────────────────────────────
private val AzulPrimario  = Color(0xFF1565C0)
private val AzulClaro     = Color(0xFF1E88E5)
private val RojoUrgente   = Color(0xFFC62828)
private val AmarilloAviso = Color(0xFFF57C00)
private val VerdeOk       = Color(0xFF2E7D32)
private val GrisNeutro    = Color(0xFF546E7A)
private val AzulFondo     = Color(0xFFF0F4FF)

// ── Pantalla principal ────────────────────────────────────────────────────────
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AgendaExpedientesScreen(
    navController: NavController,
    viewModel: AgendaExpedientesViewModel = androidx.lifecycle.viewmodel.compose.viewModel { AgendaExpedientesViewModel() }
) {
    val expedientesFiltrados by viewModel.expedientesFiltradosPorEstado.collectAsState()
    val loading              by viewModel.loading.collectAsState()
    val mostrarArchivados    by viewModel.mostrarArchivados.collectAsState()
    val stats                by viewModel.dashboardStats.collectAsState()
    val filtros              by viewModel.filtros.collectAsState()
    


    var expedienteAEliminar by remember { mutableStateOf<ExpedienteUI?>(null) }
    var mostrarFiltros      by remember { mutableStateOf(false) }

    // ── SOLUCIÓN BUG DOBLE CLICK Y VERIFICACIÓN EMAIL ──
    val navBackStackEntry by navController.currentBackStackEntryAsState()
    val isLoggedIn = remember(navBackStackEntry) { FirebaseProxy.isLoggedIn() }

    // Refrescar expedientes si el usuario está logueado
    LaunchedEffect(isLoggedIn) {
        if (isLoggedIn) {
            viewModel.cargarExpedientes()
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        if (mostrarArchivados) "Archivo" else "Agenda Judicial",
                        fontWeight = FontWeight.Bold,
                        color = Color.White
                    )
                },
                navigationIcon = {
                    IconButton(onClick = {
                        if (mostrarArchivados) viewModel.setMostrarArchivados(false)
                        else navController.popBackStack()
                    }) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Atrás", tint = Color.White)
                    }
                },
                actions = {
                    if (isLoggedIn) {
                        IconButton(onClick = { mostrarFiltros = !mostrarFiltros }) {
                            Icon(
                                Icons.Default.FilterList,
                                contentDescription = "Filtros",
                                tint = if (filtros != FiltrosAgenda()) Color(0xFFFFD54F) else Color.White
                            )
                        }
                        IconButton(onClick = { viewModel.setMostrarArchivados(!mostrarArchivados) }) {
                            Icon(
                                if (mostrarArchivados) Icons.Default.Inventory else Icons.Default.Archive,
                                contentDescription = if (mostrarArchivados) "Ver Activos" else "Ver Archivo",
                                tint = Color.White
                            )
                        }
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = AzulPrimario)
            )
        },
        floatingActionButton = {
            if (isLoggedIn && !mostrarArchivados) {
                FloatingActionButton(
                    onClick = { navController.navigate(Routes.NUEVO_EXPEDIENTE) },
                    containerColor = AzulPrimario,
                    contentColor = Color.White,
                    shape = RoundedCornerShape(16.dp)
                ) {
                    Icon(Icons.Default.Add, contentDescription = "Nuevo Expediente")
                }
            }
        }
    ) { paddingValues ->
        Box(modifier = Modifier.fillMaxSize().padding(paddingValues)) {

            if (!isLoggedIn) {
                PantallaNoAcceso(navController, isLoggedIn = false, needsVerification = false)
                return@Box
            }

            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(bottom = 88.dp)
            ) {
                // Panel de filtros expandible
                item {
                    AnimatedVisibility(
                        visible = mostrarFiltros,
                        enter = expandVertically(),
                        exit = shrinkVertically()
                    ) {
                        PanelFiltros(
                            filtros = filtros,
                            onBusqueda = viewModel::setBusqueda,
                            onFuero = viewModel::setFiltroFuero,
                            onPrioridad = viewModel::setFiltroPrioridad,
                            onToggleUrgentes = viewModel::toggleSoloUrgentes,
                            onLimpiar = viewModel::limpiarFiltros
                        )
                    }
                }

                // Dashboard de estadísticas
                if (!mostrarArchivados) {
                    item {
                        DashboardStatsRow(
                            stats = stats,
                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp)
                        )
                    }
                }

                // Loading
                if (loading) {
                    item {
                        Box(
                            Modifier.fillMaxWidth().padding(32.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator(color = AzulPrimario)
                        }
                    }
                    return@LazyColumn
                }

                // Lista vacía
                if (expedientesFiltrados.isEmpty()) {
                    item {
                        ListaVacia(
                            mostrarArchivados = mostrarArchivados,
                            hayFiltros = filtros != FiltrosAgenda()
                        )
                    }
                    return@LazyColumn
                }

                // Contador
                item {
                    Text(
                        "${expedientesFiltrados.size} expediente${if (expedientesFiltrados.size != 1) "s" else ""}",
                        style = MaterialTheme.typography.labelMedium,
                        color = GrisNeutro,
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp)
                    )
                }

                // Tarjetas
                items(expedientesFiltrados, key = { it.id }) { expediente ->
                    ExpedienteCard(
                        expediente = expediente,
                        onClick = { navController.navigate(Routes.detalleExpediente(expediente.id)) },
                        onArchivar = { viewModel.archivarExpediente(expediente.id, !expediente.archivado) },
                        onEliminar = { expedienteAEliminar = expediente },
                        isReadOnly = false,
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 6.dp)
                    )
                }
            }
        }

        // Diálogo eliminación
        if (expedienteAEliminar != null) {
            AlertDialog(
                onDismissRequest = { expedienteAEliminar = null },
                icon = { Icon(Icons.Default.DeleteForever, null, tint = RojoUrgente) },
                title = { Text("Eliminar Expediente") },
                text = {
                    Text(
                        "¿Seguro que querés eliminar \"${expedienteAEliminar?.caratula}\"?\nEsta acción no se puede deshacer.",
                        textAlign = TextAlign.Center
                    )
                },
                confirmButton = {
                    Button(
                        onClick = {
                            expedienteAEliminar?.let { viewModel.eliminarExpediente(it.id) }
                            expedienteAEliminar = null
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = RojoUrgente),
                        shape = RoundedCornerShape(10.dp)
                    ) { Text("Eliminar") }
                },
                dismissButton = {
                    TextButton(onClick = { expedienteAEliminar = null }) { Text("Cancelar") }
                },
                shape = RoundedCornerShape(16.dp)
            )
        }
    }
}

// ── Dashboard de estadísticas ─────────────────────────────────────────────────
@Composable
private fun DashboardStatsRow(stats: DashboardStats, modifier: Modifier = Modifier) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        StatCard(
            valor = stats.totalActivos.toString(),
            label = "Activos",
            icono = Icons.Default.Folder,
            color = AzulPrimario,
            modifier = Modifier.weight(1f)
        )
        StatCard(
            valor = stats.urgentes.toString(),
            label = "Urgentes",
            icono = Icons.Default.Warning,
            color = RojoUrgente,
            modifier = Modifier.weight(1f)
        )
        StatCard(
            valor = stats.conVencimientoProximo.toString(),
            label = "Próx. venc.",
            icono = Icons.Default.Schedule,
            color = AmarilloAviso,
            modifier = Modifier.weight(1f)
        )
        StatCard(
            valor = stats.archivados.toString(),
            label = "Archivados",
            icono = Icons.Default.Archive,
            color = GrisNeutro,
            modifier = Modifier.weight(1f)
        )
    }
}

@Composable
private fun StatCard(
    valor: String,
    label: String,
    icono: ImageVector,
    color: Color,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = color.copy(alpha = 0.08f)),
        elevation = CardDefaults.cardElevation(0.dp)
    ) {
        Column(
            modifier = Modifier.padding(10.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Icon(icono, null, tint = color, modifier = Modifier.size(20.dp))
            Text(
                valor,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = color
            )
            Text(
                label,
                style = MaterialTheme.typography.labelSmall,
                color = color.copy(alpha = 0.8f),
                textAlign = TextAlign.Center
            )
        }
    }
}

// ── Panel de filtros ──────────────────────────────────────────────────────────
@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun PanelFiltros(
    filtros: FiltrosAgenda,
    onBusqueda: (String) -> Unit,
    onFuero: (String) -> Unit,
    onPrioridad: (String) -> Unit,
    onToggleUrgentes: () -> Unit,
    onLimpiar: () -> Unit
) {
    Surface(color = AzulFondo, modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            OutlinedTextField(
                value = filtros.busqueda,
                onValueChange = onBusqueda,
                placeholder = { Text("Buscar por carátula, cliente, juzgado...") },
                leadingIcon = { Icon(Icons.Default.Search, null, tint = AzulPrimario) },
                trailingIcon = {
                    if (filtros.busqueda.isNotBlank()) {
                        IconButton(onClick = { onBusqueda("") }) {
                            Icon(Icons.Default.Clear, null)
                        }
                    }
                },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AzulPrimario,
                    unfocusedBorderColor = AzulPrimario.copy(alpha = 0.3f)
                )
            )

            LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                item {
                    FiltroDropdown(
                        label = if (filtros.fuero.isBlank()) "Fuero" else filtros.fuero,
                        opciones = listOf("") + FuerosJudiciales.todos,
                        seleccionado = filtros.fuero,
                        onSeleccionar = onFuero,
                        activo = filtros.fuero.isNotBlank()
                    )
                }
                item {
                    FiltroDropdown(
                        label = if (filtros.prioridad.isBlank()) "Prioridad" else filtros.prioridad,
                        opciones = listOf("", "Alta", "Normal", "Baja"),
                        seleccionado = filtros.prioridad,
                        onSeleccionar = onPrioridad,
                        activo = filtros.prioridad.isNotBlank()
                    )
                }
                item {
                    FilterChip(
                        selected = filtros.soloUrgentes,
                        onClick = onToggleUrgentes,
                        label = { Text("Solo urgentes") },
                        leadingIcon = {
                            Icon(
                                if (filtros.soloUrgentes) Icons.Default.Check else Icons.Default.Warning,
                                null,
                                modifier = Modifier.size(16.dp)
                            )
                        },
                        colors = FilterChipDefaults.filterChipColors(
                            selectedContainerColor = RojoUrgente.copy(alpha = 0.15f),
                            selectedLabelColor = RojoUrgente
                        )
                    )
                }
                if (filtros != FiltrosAgenda()) {
                    item {
                        AssistChip(
                            onClick = onLimpiar,
                            label = { Text("Limpiar") },
                            leadingIcon = {
                                Icon(Icons.Default.FilterListOff, null, modifier = Modifier.size(16.dp))
                            }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun FiltroDropdown(
    label: String,
    opciones: List<String>,
    seleccionado: String,
    onSeleccionar: (String) -> Unit,
    activo: Boolean
) {
    var expanded by remember { mutableStateOf(false) }
    Box {
        FilterChip(
            selected = activo,
            onClick = { expanded = true },
            label = { Text(label, maxLines = 1) },
            trailingIcon = {
                Icon(Icons.Default.ArrowDropDown, null, modifier = Modifier.size(16.dp))
            },
            colors = FilterChipDefaults.filterChipColors(
                selectedContainerColor = AzulPrimario.copy(alpha = 0.15f),
                selectedLabelColor = AzulPrimario
            )
        )
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            opciones.forEach { opcion ->
                DropdownMenuItem(
                    text = { Text(if (opcion.isBlank()) "Todos" else opcion) },
                    onClick = { onSeleccionar(opcion); expanded = false },
                    leadingIcon = {
                        if (opcion == seleccionado) {
                            Icon(
                                Icons.Default.Check,
                                null,
                                tint = AzulPrimario,
                                modifier = Modifier.size(16.dp)
                            )
                        }
                    }
                )
            }
        }
    }
}

// ── Tarjeta de expediente ─────────────────────────────────────────────────────
@Composable
fun ExpedienteCard(
    expediente: ExpedienteUI,
    onClick: () -> Unit,
    onArchivar: () -> Unit,
    onEliminar: () -> Unit,
    isReadOnly: Boolean = false,
    modifier: Modifier = Modifier
) {
    val prioridadColor = when (expediente.prioridad) {
        "Alta" -> RojoUrgente
        "Baja" -> VerdeOk
        else   -> AzulPrimario
    }
    val borderColor = when {
        expediente.archivado                  -> Color.Transparent
        expediente.tieneUrgencia              -> RojoUrgente
        expediente.proximoVencimiento != null -> AmarilloAviso
        else                                  -> Color.Transparent
    }

    Card(
        modifier = modifier
            .fillMaxWidth()
            .clickable { onClick() }
            .then(
                if (borderColor != Color.Transparent)
                    Modifier.border(1.5.dp, borderColor, RoundedCornerShape(16.dp))
                else Modifier
            ),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(
            containerColor = when {
                expediente.archivado     -> Color(0xFFF5F5F5)
                expediente.tieneUrgencia -> Color(0xFFFFF3F3)
                else                     -> MaterialTheme.colorScheme.surface
            }
        ),
        elevation = CardDefaults.cardElevation(
            defaultElevation = if (expediente.tieneUrgencia) 4.dp else 2.dp
        )
    ) {
        Column(modifier = Modifier.padding(16.dp)) {

            Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.Top) {
                // Barra de prioridad
                Box(
                    modifier = Modifier
                        .width(4.dp)
                        .height(48.dp)
                        .clip(RoundedCornerShape(2.dp))
                        .background(if (expediente.archivado) Color.LightGray else prioridadColor)
                )
                Spacer(Modifier.width(12.dp))
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = expediente.caratula,
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        color = if (expediente.archivado) Color.Gray else MaterialTheme.colorScheme.onSurface
                    )
                    if (expediente.nroExpediente.isNotBlank()) {
                        Text(
                            "Exp. ${expediente.nroExpediente}",
                            style = MaterialTheme.typography.labelSmall,
                            color = GrisNeutro
                        )
                    }
                }

                var showMenu by remember { mutableStateOf(false) }
                if (!isReadOnly) {
                    Box {
                        IconButton(onClick = { showMenu = true }, modifier = Modifier.size(32.dp)) {
                            Icon(Icons.Default.MoreVert, null, tint = GrisNeutro, modifier = Modifier.size(18.dp))
                        }
                        DropdownMenu(expanded = showMenu, onDismissRequest = { showMenu = false }) {
                            DropdownMenuItem(
                                text = { Text(if (expediente.archivado) "Restaurar" else "Archivar") },
                                onClick = { showMenu = false; onArchivar() },
                                leadingIcon = {
                                    Icon(
                                        if (expediente.archivado) Icons.Default.Unarchive else Icons.Default.Archive,
                                        null
                                    )
                                }
                            )
                            DropdownMenuItem(
                                text = { Text("Eliminar", color = RojoUrgente) },
                                onClick = { showMenu = false; onEliminar() },
                                leadingIcon = { Icon(Icons.Default.Delete, null, tint = RojoUrgente) }
                            )
                        }
                    }
                }
            }

            Spacer(Modifier.height(10.dp))

            // Chips de metadata
            LazyRow(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                if (expediente.fuero.isNotBlank()) {
                    item {
                        MetaChip(texto = expediente.fuero, icono = Icons.Default.Gavel, color = AzulPrimario)
                    }
                }
                if (expediente.cliente.isNotBlank()) {
                    item {
                        MetaChip(texto = expediente.cliente, icono = Icons.Default.Person, color = GrisNeutro)
                    }
                }
                if (expediente.juzgado.isNotBlank()) {
                    item {
                        MetaChip(
                            texto = expediente.juzgado,
                            icono = Icons.Default.AccountBalance,
                            color = GrisNeutro
                        )
                    }
                }
                if (expediente.cantMovimientos > 0) {
                    item {
                        MetaChip(
                            texto = "${expediente.cantMovimientos} mov.",
                            icono = Icons.Default.Timeline,
                            color = AzulClaro
                        )
                    }
                }
            }

            Spacer(Modifier.height(10.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Surface(
                    color = AzulPrimario.copy(alpha = if (expediente.archivado) 0.05f else 0.1f),
                    shape = RoundedCornerShape(6.dp)
                ) {
                    Text(
                        text = expediente.estadoProcesal.ifBlank { "Sin estado" },
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp),
                        style = MaterialTheme.typography.labelSmall,
                        color = if (expediente.archivado) Color.Gray else AzulPrimario,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }

                if (!expediente.archivado) {
                    when {
                        expediente.tieneUrgencia -> {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(4.dp)
                            ) {
                                Icon(
                                    Icons.Default.Warning,
                                    null,
                                    tint = RojoUrgente,
                                    modifier = Modifier.size(14.dp)
                                )
                                Text(
                                    "URGENTE · ${expediente.proximoVencimiento?.let { formatearFecha(it) } ?: ""}",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = RojoUrgente,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                        expediente.proximoVencimiento != null -> {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(4.dp)
                            ) {
                                Icon(
                                    Icons.Default.CalendarToday,
                                    null,
                                    tint = AmarilloAviso,
                                    modifier = Modifier.size(14.dp)
                                )
                                Text(
                                    formatearFecha(expediente.proximoVencimiento),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = AmarilloAviso
                                )
                            }
                        }
                        else -> {
                            Text(
                                "Sin vencimientos",
                                style = MaterialTheme.typography.labelSmall,
                                color = Color.Gray
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MetaChip(texto: String, icono: ImageVector, color: Color) {
    Surface(color = color.copy(alpha = 0.08f), shape = RoundedCornerShape(20.dp)) {
        Row(
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Icon(icono, null, tint = color, modifier = Modifier.size(12.dp))
            Text(texto, style = MaterialTheme.typography.labelSmall, color = color, maxLines = 1)
        }
    }
}

// ── Pantalla no acceso (Login o Verificación) ────────────────────────────────
@Composable
private fun PantallaNoAcceso(navController: NavController, isLoggedIn: Boolean, needsVerification: Boolean) {
    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        Card(
            modifier = Modifier.fillMaxWidth().padding(24.dp),
            shape = RoundedCornerShape(20.dp),
            colors = CardDefaults.cardColors(containerColor = AzulFondo)
        ) {
            Column(
                modifier = Modifier.padding(28.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Box(
                    modifier = Modifier
                        .size(72.dp)
                        .clip(CircleShape)
                        .background(AzulPrimario.copy(alpha = 0.1f)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        if (needsVerification) Icons.Default.Email else Icons.Default.Lock, 
                        null, 
                        tint = AzulPrimario, 
                        modifier = Modifier.size(36.dp)
                    )
                }
                Text(
                    if (needsVerification) "Verificar Email" else "Agenda Judicial",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = AzulPrimario
                )
                Text(
                    if (needsVerification) "Tu cuenta aún no está verificada. Revisa tu email para activar el acceso completo."
                    else "Iniciá sesión para acceder a tus expedientes, movimientos procesales y vencimientos.",
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center,
                    color = GrisNeutro
                )
                Button(
                    onClick = { navController.navigate(Routes.login(register = false)) },
                    modifier = Modifier.fillMaxWidth().height(50.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = AzulPrimario),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Icon(if (needsVerification) Icons.Default.Email else Icons.Default.Login, null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text(if (needsVerification) "Ir a Verificación" else "Iniciar Sesión", fontWeight = FontWeight.Bold)
                }
                if (!needsVerification) {
                    TextButton(onClick = { navController.navigate(Routes.login(register = true)) }) {
                        Text("¿No tenés cuenta? Registrate aquí", color = AzulPrimario, fontSize = 13.sp)
                    }
                }
            }
        }
    }
}

// ── Lista vacía ───────────────────────────────────────────────────────────────
@Composable
private fun ListaVacia(mostrarArchivados: Boolean, hayFiltros: Boolean) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(40.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Icon(
            when {
                hayFiltros        -> Icons.Default.SearchOff
                mostrarArchivados -> Icons.Default.Inventory2
                else              -> Icons.Default.FolderOpen
            },
            null,
            modifier = Modifier.size(64.dp),
            tint = Color.LightGray
        )
        Text(
            when {
                hayFiltros        -> "Sin resultados para los filtros aplicados"
                mostrarArchivados -> "No hay expedientes archivados"
                else              -> "No hay expedientes activos"
            },
            style = MaterialTheme.typography.bodyLarge,
            color = Color.Gray,
            textAlign = TextAlign.Center
        )
        if (!mostrarArchivados && !hayFiltros) {
            Text(
                "Tocá el botón + para crear tu primer expediente",
                style = MaterialTheme.typography.bodySmall,
                color = Color.LightGray,
                textAlign = TextAlign.Center
            )
        }
    }
}
